import { registerComponent, localizeRedirect } from "helpers";
import { WYATTElement } from "wyatt";

registerComponent(
  "search-page",
  class SearchPage extends WYATTElement {
    constructor() {
      super();

      this.form = this.querySelector("form");

      this.paramsToUseFromUrl = new URLSearchParams(window.location.search);

      this.fetchResults();

      this.form.addEventListener("change", this.handleChange.bind(this));

      window.addEventListener("popstate", this.handlePopstate.bind(this));

      this.subscribe(
        WYATT.PubSub.events.updateSearchHistory,
        this.updateHistory.bind(this),
      );

      this.subscribe(
        WYATT.PubSub.events.clearAllFilters,
        this.handleClearAll.bind(this),
      );
    }

    get formData() {
      return new FormData(this.form);
    }

    get formParams() {
      return new URLSearchParams(this.formData);
    }

    get requestParams() {
      const hiddenParams = new URLSearchParams(this.config.hiddenParams);
      const trackingParams = new URLSearchParams(WYATT.searchspring.tracking);
      const { formParams } = this;

      const urlParams = new URLSearchParams(this.paramsToUseFromUrl);
      [...formParams.keys()].forEach((key) => urlParams.delete(key));

      const params = new URLSearchParams([
        ...hiddenParams,
        ...formParams,
        ...urlParams,
        ...trackingParams,
      ]);

      params.set("domain", window.location.href);

      // Required for Searchspring to return a beacon-compatible responseId; the
      // beacon events reference it, so without this they can't be correlated.
      params.set("beacon", "true");

      return params;
    }

    get pageUrl() {
      const params = new URLSearchParams(this.formParams);

      params.delete("siteId");

      return `${window.location.pathname}?${params.toString()}`;
    }

    handleChange(event) {
      event.preventDefault();

      this.paramsToUseFromUrl = [];
      this.resetPage();

      this.fetchResults();
    }

    handlePopstate(event) {
      event.preventDefault();

      this.fetchResults();
    }

    handleClearAll() {
      this.resetPage();

      const params = new URLSearchParams(this.requestParams);

      Array.from(params.keys())
        .filter((key) => key.startsWith("filter."))
        .forEach((key) => params.delete(key));

      this.fetchResults(params);
    }

    // A filter, sort or Clear all gives a new result set, so the current page
    // number can point past its end. Pagination calls fetchResults directly
    // rather than dispatching a form change, so it keeps its page.
    resetPage() {
      this.form.querySelector('input[name="page"]').value = 1;
    }

    async fetchResults(params = this.requestParams) {
      this.setAttribute("data-loading", "");
      this.setAttribute("aria-busy", "true");

      // Collection pages call the category.json endpoint and keyword search
      // calls search.json; the surface is set per-section in the data-config
      // (`api`). Default to search for any section that omits it.
      const endpoint =
        WYATT.config.searchspring.endpoints[this.config.api || "search"];

      const requestUrl = [
        "https://",
        `${params.get("siteId")}.`,
        `${endpoint}?`,
        params.toString(),
      ].join("");

      try {
        const response = await fetch(
          requestUrl,
          WYATT.config.searchspring.options.get,
        );

        if (!response.ok) {
          throw new Error(`Search request failed: ${response.status}`);
        }

        const data = await response.json();

        const destination = localizeRedirect(data.merchandising?.redirect);
        if (destination) {
          // This returns before handleUpdate(), so the usual searchResultsFetched
          // push never happens and the search would go unrecorded. PubSub is
          // synchronous, so subscribers run before the navigation below.
          //
          // This component also drives collection grids, where there is no `q`
          // and a category-level redirect is not a search. The subscriber drops
          // events with an empty term, so those stay unreported by design.
          // Guarded because this sits on the shopper's path to the collection
          // page: the enclosing try would swallow a throw from here and skip
          // the redirect below, so a tracking fault would strand the search.
          try {
            this.publish(WYATT.PubSub.events.searchRedirected, {
              searchTerm:
                new URLSearchParams(window.location.search).get("q") || "",
              destination,
              source: "searchspring",
            });
          } catch (trackingError) {
            console.error(trackingError);
          }

          window.location.href = destination;
          return;
        }

        this.handleUpdate(data);
      } catch (error) {
        console.error(error);
      } finally {
        this.removeAttribute("data-loading");
        this.setAttribute("aria-busy", "false");
      }
    }

    handleUpdate(data) {
      this.dataset.ssResponseId = (data && data.responseId) || "";

      this.publish(WYATT.PubSub.events.searchResultsFetched, data);

      this.fireBeaconRender(data);
      this.updateTitle(data);
      this.updateHistory();
    }

    // Fire a single render per response at the page level. Gated to the products
    // siteId (beacon is products-only) and done here rather than in results-grid
    // so multiple grids on the page don't each emit a render.
    fireBeaconRender(data) {
      if (!WYATT.beacon) return;

      if (
        this.formData.get("siteId") !==
        WYATT.config.searchspring.siteIds.products
      ) {
        return;
      }

      const surface = WYATT.beacon.detectSurface(this);
      if (!surface) return;

      WYATT.beacon[surface].render({
        responseId: (data && data.responseId) || "",
      });
    }

    updateTitle(data) {
      if (!this.config.title) {
        return;
      }

      document.title = this.config.title
        .replace("{{ count }}", data.pagination.totalResults)
        .replace("{{ query }}", `"${this.formData.get("q")}"`);
    }

    updateHistory() {
      window.history.pushState({}, "", this.pageUrl);

      // Keep the SDK's context URL current: searches update the URL via
      // pushState without a reload, so beacon events would otherwise carry the
      // page's initial URL.
      if (window.athos && window.athos.tracker) {
        window.athos.tracker.updateContext("pageUrl", window.location.href);
      }
    }
  },
);
